import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PaymentTypeOption {
  type_id: string;
  name: string;
}

interface PaymentCardProps {
  data: {
    payment_types: PaymentTypeOption[];
    suggested_amount?: number;
    currency?: string;
  };
  onSelect: (selection: Record<string, unknown>) => void;
}

export function PaymentCard({ data, onSelect }: PaymentCardProps) {
  const { t } = useTranslation();
  const [paymentType, setPaymentType] = useState(data.payment_types[0]?.type_id || '');
  const [amount, setAmount] = useState(data.suggested_amount?.toString() || '');
  const [reference, setReference] = useState('');
  const currency = data.currency || 'ILS';

  const handleConfirm = () => {
    if (!paymentType || !amount) return;
    onSelect({
      payment_type: paymentType,
      amount: parseFloat(amount),
      reference: reference || undefined,
      currency,
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden my-2">
      <div className="px-4 py-2.5 bg-gray-50 border-b">
        <span className="text-sm font-medium text-gray-700">
          {t('agentChat.cards.paymentDetails', 'Payment Details')}
        </span>
      </div>
      <div className="p-4 space-y-3">
        {/* Payment type */}
        <div>
          <label className="text-xs text-gray-500 block mb-1">
            {t('agentChat.cards.paymentType', 'Payment Type')}
          </label>
          <select
            value={paymentType}
            onChange={(e) => setPaymentType(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {data.payment_types.map((pt) => (
              <option key={pt.type_id} value={pt.type_id}>
                {pt.name}
              </option>
            ))}
          </select>
        </div>

        {/* Amount */}
        <div>
          <label className="text-xs text-gray-500 block mb-1">
            {t('agentChat.cards.amount', 'Amount')} ({currency === 'ILS' ? '₪' : currency})
          </label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500"
            min={0}
            step={0.01}
          />
        </div>

        {/* Reference */}
        <div>
          <label className="text-xs text-gray-500 block mb-1">
            {t('agentChat.cards.reference', 'Reference / Check #')} ({t('common.optional', 'optional')})
          </label>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <button
          onClick={handleConfirm}
          disabled={!paymentType || !amount}
          className="w-full py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors"
        >
          {t('agentChat.cards.confirmPayment', 'Confirm Payment')}
        </button>
      </div>
    </div>
  );
}
